import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const NeedsMatrix = () => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(0);
  const current = t.needs.items[selected];

  return (
    <section id="necesidades" className="relative border-y border-cy/10 bg-panel py-24 lg:py-32" data-testid="needs-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-cy">{t.needs.label}</p>
          <h2 className="mt-4 font-display text-2xl font-bold leading-[1.15] tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t.needs.title}
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">{t.needs.sub}</p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-12">
          <Reveal className="flex flex-col gap-2">
            {t.needs.items.map((item, i) => (
              <button
                key={item.slug}
                data-testid={`needs-matrix-tab-${item.slug}`}
                onClick={() => setSelected(i)}
                className={`group flex items-center justify-between rounded-lg border px-5 py-4 text-left transition-all duration-300 ${
                  selected === i
                    ? "border-cy/50 bg-cy/10 text-slate-50"
                    : "border-cy/10 bg-qcard/60 text-slate-400 hover:border-cy/30 hover:text-slate-200"
                }`}
              >
                <span className="text-sm font-medium sm:text-base">{item.q}</span>
                <ChevronRight
                  className={`h-4 w-4 shrink-0 transition-all duration-300 ${
                    selected === i ? "translate-x-0 text-cy opacity-100" : "-translate-x-1 opacity-0 group-hover:opacity-60"
                  }`}
                />
              </button>
            ))}
          </Reveal>

          <Reveal delay={0.15} className="relative">
            <div className="sticky top-28 overflow-hidden rounded-xl border border-cy/20 bg-ink/70 p-8 sm:p-10">
              <div className="grid-bg pointer-events-none absolute inset-0 opacity-60" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={selected}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                  data-testid="needs-matrix-solution"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-cy">{t.needs.solutionLabel}</p>
                  <h3 className="mt-4 font-display text-3xl font-extrabold text-slate-50 sm:text-4xl">{current.a}</h3>
                  <p className="mt-5 text-base leading-relaxed text-slate-300">{current.d}</p>
                  <div className="mt-8 flex items-center gap-3 text-slate-500">
                    <span className="max-w-[45%] truncate rounded-md border border-cy/15 bg-qcard px-3 py-2 font-mono text-[11px]">{current.q}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-cy" />
                    <span className="rounded-md border border-em/30 bg-em/10 px-3 py-2 font-mono text-[11px] text-em">{current.a}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default NeedsMatrix;
